/**
 * Layout Algorithms for Schema GraphOS
 * Implements various graph layout algorithms for entity organization
 */

// Constants
const DEFAULT_NODE_WIDTH = 300;
const DEFAULT_NODE_HEIGHT = 200;
const DEFAULT_HORIZONTAL_SPACING = 100;
const DEFAULT_VERTICAL_SPACING = 150;
const DEFAULT_START_X = 100;
const DEFAULT_START_Y = 100;

/**
 * 1. HIERARCHICAL LAYOUT (BFS-based)
 * Organizes nodes in hierarchical levels based on relationships
 */
export const hierarchicalLayout = (nodes, edges) => {
  const nodeChildren = new Map();
  const nodeParents = new Map();
  const nodeLevels = new Map();

  // Initialize maps
  nodes.forEach(node => {
    nodeChildren.set(node.id, []);
    nodeParents.set(node.id, []);
  });

  // Build parent-child relationships
  edges.forEach(edge => {
    const children = nodeChildren.get(edge.source) || [];
    children.push(edge.target);
    nodeChildren.set(edge.source, children);

    const parents = nodeParents.get(edge.target) || [];
    parents.push(edge.source);
    nodeParents.set(edge.target, parents);
  });

  // Find root nodes (no parents)
  const rootNodes = nodes.filter(node => 
    (nodeParents.get(node.id) || []).length === 0
  );

  // If no root nodes, use nodes with minimal parents
  const startNodes = rootNodes.length > 0 
    ? rootNodes 
    : [nodes.reduce((min, node) => 
        (nodeParents.get(node.id) || []).length < (nodeParents.get(min.id) || []).length ? node : min
      )];

  // Assign levels using BFS
  const visited = new Set();
  const queue = startNodes.map(node => ({ node, level: 0 }));

  while (queue.length > 0) {
    const { node, level } = queue.shift();
    
    if (visited.has(node.id)) continue;
    visited.add(node.id);

    nodeLevels.set(node.id, level);

    // Add children to next level
    const children = nodeChildren.get(node.id) || [];
    children.forEach(childId => {
      const childNode = nodes.find(n => n.id === childId);
      if (childNode && !visited.has(childId)) {
        queue.push({ node: childNode, level: level + 1 });
      }
    });
  }

  // Handle disconnected nodes
  nodes.forEach(node => {
    if (!visited.has(node.id)) {
      nodeLevels.set(node.id, 0);
    }
  });

  // Group nodes by level
  const levels = new Map();
  nodes.forEach(node => {
    const level = nodeLevels.get(node.id) || 0;
    if (!levels.has(level)) {
      levels.set(level, []);
    }
    levels.get(level).push(node);
  });

  // Position nodes
  const updatedNodes = [];
  const maxLevel = Math.max(...Array.from(levels.keys()));

  for (let level = 0; level <= maxLevel; level++) {
    const nodesInLevel = levels.get(level) || [];
    const levelWidth = nodesInLevel.length * (DEFAULT_NODE_WIDTH + DEFAULT_HORIZONTAL_SPACING) - DEFAULT_HORIZONTAL_SPACING;
    const levelStartX = DEFAULT_START_X - (levelWidth / 2) + (levelWidth / nodesInLevel.length / 2);

    nodesInLevel.forEach((node, index) => {
      updatedNodes.push({
        ...node,
        position: {
          x: levelStartX + index * (DEFAULT_NODE_WIDTH + DEFAULT_HORIZONTAL_SPACING),
          y: DEFAULT_START_Y + level * (DEFAULT_NODE_HEIGHT + DEFAULT_VERTICAL_SPACING)
        }
      });
    });
  }

  return updatedNodes;
};

/**
 * 2. SUGIYAMA LAYOUT (Layered with crossing reduction)
 * Similar to hierarchical but with better edge crossing reduction
 */
export const sugiyamaLayout = (nodes, edges) => {
  // Phase 1: Assign layers (same as hierarchical)
  const layeredNodes = hierarchicalLayout(nodes, edges);
  
  // Phase 2: Minimize crossings by reordering nodes within layers
  const layers = new Map();
  layeredNodes.forEach(node => {
    const y = node.position.y;
    if (!layers.has(y)) {
      layers.set(y, []);
    }
    layers.get(y).push(node);
  });

  // Sort layers by Y position
  const sortedLayers = Array.from(layers.entries()).sort((a, b) => a[0] - b[0]);

  // Apply barycenter heuristic for crossing reduction
  for (let i = 1; i < sortedLayers.length; i++) {
    const currentLayer = sortedLayers[i][1];
    
    currentLayer.forEach(node => {
      // Calculate barycenter (average position of connected nodes in previous layer)
      const connectedParents = edges
        .filter(e => e.target === node.id)
        .map(e => layeredNodes.find(n => n.id === e.source))
        .filter(n => n && n.position.y < node.position.y);

      if (connectedParents.length > 0) {
        const avgX = connectedParents.reduce((sum, parent) => sum + parent.position.x, 0) / connectedParents.length;
        node.barycenter = avgX;
      } else {
        node.barycenter = node.position.x;
      }
    });

    // Sort layer by barycenter
    currentLayer.sort((a, b) => a.barycenter - b.barycenter);

    // Reposition nodes in layer
    const levelWidth = currentLayer.length * (DEFAULT_NODE_WIDTH + DEFAULT_HORIZONTAL_SPACING) - DEFAULT_HORIZONTAL_SPACING;
    const levelStartX = DEFAULT_START_X - (levelWidth / 2) + (levelWidth / currentLayer.length / 2);

    currentLayer.forEach((node, index) => {
      node.position.x = levelStartX + index * (DEFAULT_NODE_WIDTH + DEFAULT_HORIZONTAL_SPACING);
      delete node.barycenter; // Clean up temp property
    });
  }

  return layeredNodes;
};

/**
 * 3. FORCE-DIRECTED LAYOUT
 * Simulates physical forces between nodes
 */
export const forceDirectedLayout = (nodes, edges, iterations = 200) => {
  // Configuration
  const repulsionStrength = 50000;
  const attractionStrength = 0.01;
  const damping = 0.85;
  const centerGravity = 0.02;

  // Initialize with random positions or current positions
  const layoutNodes = nodes.map(node => ({
    ...node,
    position: node.position || {
      x: DEFAULT_START_X + Math.random() * 1000,
      y: DEFAULT_START_Y + Math.random() * 800
    },
    velocity: { x: 0, y: 0 }
  }));

  const centerX = DEFAULT_START_X + 500;
  const centerY = DEFAULT_START_Y + 400;

  // Run simulation
  for (let iter = 0; iter < iterations; iter++) {
    const temperature = 1 - (iter / iterations); // Cooling schedule

    // Calculate forces
    layoutNodes.forEach(node => {
      let fx = 0;
      let fy = 0;

      // Repulsion from all other nodes
      layoutNodes.forEach(other => {
        if (node.id === other.id) return;

        const dx = node.position.x - other.position.x;
        const dy = node.position.y - other.position.y;
        const distanceSquared = dx * dx + dy * dy;
        const distance = Math.sqrt(distanceSquared) || 1;

        const repulsion = repulsionStrength / distanceSquared;
        fx += (dx / distance) * repulsion;
        fy += (dy / distance) * repulsion;
      });

      // Attraction along edges
      edges.forEach(edge => {
        let other = null;
        let isSource = false;

        if (edge.source === node.id) {
          other = layoutNodes.find(n => n.id === edge.target);
          isSource = true;
        } else if (edge.target === node.id) {
          other = layoutNodes.find(n => n.id === edge.source);
        }

        if (other) {
          const dx = other.position.x - node.position.x;
          const dy = other.position.y - node.position.y;
          const distance = Math.sqrt(dx * dx + dy * dy) || 1;

          const attraction = distance * attractionStrength;
          fx += (dx / distance) * attraction;
          fy += (dy / distance) * attraction;
        }
      });

      // Gravity towards center
      const dcx = centerX - node.position.x;
      const dcy = centerY - node.position.y;
      fx += dcx * centerGravity;
      fy += dcy * centerGravity;

      // Update velocity and position
      node.velocity.x = (node.velocity.x + fx) * damping;
      node.velocity.y = (node.velocity.y + fy) * damping;

      node.position.x += node.velocity.x * temperature;
      node.position.y += node.velocity.y * temperature;
    });
  }

  // Clean up and return
  return layoutNodes.map(node => {
    const { velocity, ...cleanNode } = node;
    return cleanNode;
  });
};

/**
 * 4. GRID LAYOUT
 * Simple grid arrangement
 */
export const gridLayout = (nodes) => {
  const cols = Math.ceil(Math.sqrt(nodes.length));
  const horizontalSpacing = DEFAULT_NODE_WIDTH + DEFAULT_HORIZONTAL_SPACING;
  const verticalSpacing = DEFAULT_NODE_HEIGHT + DEFAULT_VERTICAL_SPACING;

  return nodes.map((node, index) => {
    const col = index % cols;
    const row = Math.floor(index / cols);

    return {
      ...node,
      position: {
        x: DEFAULT_START_X + col * horizontalSpacing,
        y: DEFAULT_START_Y + row * verticalSpacing
      }
    };
  });
};

/**
 * 5. CIRCULAR LAYOUT
 * Arranges nodes in a circle
 */
export const circularLayout = (nodes) => {
  const centerX = DEFAULT_START_X + 600;
  const centerY = DEFAULT_START_Y + 400;
  const radius = Math.max(300, nodes.length * 30);

  return nodes.map((node, index) => {
    const angle = (2 * Math.PI * index) / nodes.length;
    
    return {
      ...node,
      position: {
        x: centerX + radius * Math.cos(angle),
        y: centerY + radius * Math.sin(angle)
      }
    };
  });
};

/**
 * 6. RADIAL TREE LAYOUT
 * Root at center, children radiating outward in concentric circles
 */
export const radialLayout = (nodes, edges) => {
  const nodeChildren = new Map();
  const nodeParents = new Map();
  const nodeLevels = new Map();

  // Initialize maps
  nodes.forEach(node => {
    nodeChildren.set(node.id, []);
    nodeParents.set(node.id, []);
  });

  // Build relationships
  edges.forEach(edge => {
    nodeChildren.get(edge.source).push(edge.target);
    nodeParents.get(edge.target).push(edge.source);
  });

  // Find root (node with most children or no parents)
  const rootNodes = nodes.filter(node => 
    (nodeParents.get(node.id) || []).length === 0
  );

  const root = rootNodes.length > 0
    ? rootNodes.reduce((max, node) => 
        (nodeChildren.get(node.id) || []).length > (nodeChildren.get(max.id) || []).length ? node : max
      )
    : nodes[0];

  // Calculate levels
  const visited = new Set();
  const queue = [{ node: root, level: 0 }];

  while (queue.length > 0) {
    const { node, level } = queue.shift();
    
    if (visited.has(node.id)) continue;
    visited.add(node.id);

    nodeLevels.set(node.id, level);

    const children = nodeChildren.get(node.id) || [];
    children.forEach(childId => {
      const childNode = nodes.find(n => n.id === childId);
      if (childNode && !visited.has(childId)) {
        queue.push({ node: childNode, level: level + 1 });
      }
    });
  }

  // Handle disconnected nodes
  nodes.forEach(node => {
    if (!visited.has(node.id)) {
      nodeLevels.set(node.id, 1);
    }
  });

  // Group by level
  const levels = new Map();
  nodes.forEach(node => {
    const level = nodeLevels.get(node.id) || 0;
    if (!levels.has(level)) {
      levels.set(level, []);
    }
    levels.get(level).push(node);
  });

  // Position nodes
  const centerX = DEFAULT_START_X + 600;
  const centerY = DEFAULT_START_Y + 400;
  const radiusStep = 250;

  const updatedNodes = [];
  const maxLevel = Math.max(...Array.from(levels.keys()));

  for (let level = 0; level <= maxLevel; level++) {
    const nodesInLevel = levels.get(level) || [];
    const radius = level * radiusStep;

    if (level === 0) {
      // Root at center
      nodesInLevel.forEach(node => {
        updatedNodes.push({
          ...node,
          position: { x: centerX, y: centerY }
        });
      });
    } else {
      // Children in circles
      nodesInLevel.forEach((node, index) => {
        const angle = (2 * Math.PI * index) / nodesInLevel.length;
        updatedNodes.push({
          ...node,
          position: {
            x: centerX + radius * Math.cos(angle),
            y: centerY + radius * Math.sin(angle)
          }
        });
      });
    }
  }

  return updatedNodes;
};

/**
 * Main layout dispatcher
 */
export const applyLayout = (algorithmId, nodes, edges) => {
  switch (algorithmId) {
    case 'hierarchical':
      return hierarchicalLayout(nodes, edges);
    case 'sugiyama':
      return sugiyamaLayout(nodes, edges);
    case 'force':
      return forceDirectedLayout(nodes, edges);
    case 'grid':
      return gridLayout(nodes);
    case 'circular':
      return circularLayout(nodes);
    case 'radial':
      return radialLayout(nodes, edges);
    default:
      return hierarchicalLayout(nodes, edges);
  }
};
